import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { DIRECTORY_FOUNDER_OPEN_SEAT_MATCHES } from '@src/gql/queries/DirectoryFounderOpenSeatMatches';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useMemo } from 'react';

/**
 * ADV-B3 / L13 — for the logged-in founder, load UserMatches across their open-seat
 * searches and pick, per target advisor, the seat with the highest raw matchScore.
 * Display that seat's matchScoreAsPercent with the seat name (never max of percents).
 */

export type NamedSeatMatch = {
  targetUserId: string;
  matchScore: number;
  matchScoreAsPercent: number;
  seatTitle: string;
  /** Short badge copy, e.g. "82% for Fundraising advisor" */
  badgeLabel: string;
};
function cleanSeatTitle(title?: string | null): string {
  const t = (title || '').trim();
  if (!t) {
    return 'open board seat';
  }
  return t;
}

/**
 * Build best-per-target map: first occurrence wins when items are sorted by matchScore DESC
 * (raw score pick). Percent is that seat's own matchScoreAsPercent only.
 */
export function pickNamedSeatMatchesByTarget(items: Array<{
  targetUserId?: string | null;
  matchScore?: number | null;
  matchScoreAsPercent?: number | null;
  userSearch?: {
    groupRequest?: {
      title?: string | null;
      groupRequestTypeKey?: string | null;
    } | null;
  } | null;
} | null>): Map<string, NamedSeatMatch> {
  const map = new Map<string, NamedSeatMatch>();
  for (const item of items) {
    if (!item?.targetUserId) {
      continue;
    }
    // Prefer advisor-linked searches; still accept matches without type if only one path
    const typeKey = item.userSearch?.groupRequest?.groupRequestTypeKey;
    if (typeKey && typeKey !== 'advisor') {
      continue;
    }
    if (map.has(item.targetUserId)) {
      continue;
    }
    const score = item.matchScore ?? 0;
    if (score <= 0) {
      continue;
    }
    const percent = Math.round(item.matchScoreAsPercent ?? 0);
    // Avoid implying 100% quality for a weak pool without seat context — always name the seat
    if (percent <= 0) {
      continue;
    }
    const seatTitle = cleanSeatTitle(item.userSearch?.groupRequest?.title);
    map.set(item.targetUserId, {
      targetUserId: item.targetUserId,
      matchScore: score,
      matchScoreAsPercent: percent,
      seatTitle,
      badgeLabel: `${percent}% for your ${seatTitle}`
    });
  }
  return map;
}
export function useDirectoryNamedSeatMatches() {
  const $ = _c(14);
  const {
    currentUser
  } = useCurrentUser();
  const userId = currentUser?.isRegistered && !currentUser?.isGuest ? currentUser.userId : undefined;
  const t0 = !userId;
  let t1;
  if ($[0] !== userId) {
    t1 = {
      searchingUserId: userId
    };
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      skip: t0,
      variables: t1,
      fetchPolicy: "cache-and-network"
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useQuery(DIRECTORY_FOUNDER_OPEN_SEAT_MATCHES, t2);
  query.data?.scoUserMatches?.items;
  let t3;
  if ($[5] !== query.data?.scoUserMatches?.items) {
    t3 = query.data?.scoUserMatches?.items ?? [];
    $[5] = query.data?.scoUserMatches?.items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const items = t3;
  let t4;
  if ($[7] !== items) {
    t4 = pickNamedSeatMatchesByTarget(items);
    $[7] = items;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const byTargetUserId = t4;
  const t5 = !!userId && query.loading && !query.data;
  let t6;
  if ($[9] !== byTargetUserId || $[10] !== query.error || $[11] !== query.refetch || $[12] !== t5) {
    t6 = {
      byTargetUserId,
      loading: t5,
      error: query.error,
      refetch: query.refetch
    };
    $[9] = byTargetUserId;
    $[10] = query.error;
    $[11] = query.refetch;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}